import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, useLang, formatCurrency } from "../lib/providers";
import { PageHeader, Btn, StatusBadge, EmptyState, TextInput, Select } from "../components/primitives";
import { FileText, Plus, Clock, CheckCircle, Stack, UsersThree, Tag, Receipt, CalendarBlank } from "@phosphor-icons/react";

function presetRange(kind) {
  const now = new Date();
  const ymd = (d) => d.toISOString().slice(0, 10);
  switch (kind) {
    case "today": return { from: ymd(now), to: ymd(now) };
    case "week": {
      const first = new Date(now); first.setDate(now.getDate() - now.getDay() + 1);
      return { from: ymd(first), to: ymd(now) };
    }
    case "month": {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: ymd(first), to: ymd(now) };
    }
    case "quarter": {
      const q = Math.floor(now.getMonth() / 3);
      const first = new Date(now.getFullYear(), q * 3, 1);
      return { from: ymd(first), to: ymd(now) };
    }
    case "year": {
      return { from: `${now.getFullYear()}-01-01`, to: ymd(now) };
    }
    default: return { from: "", to: "" };
  }
}

export default function DashboardPage() {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState(() => localStorage.getItem("dash_preset") || "all");
  const [range, setRange] = useState(() => {
    const p = localStorage.getItem("dash_preset") || "all";
    return p === "all" ? { from: "", to: "" } : presetRange(p);
  });

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (range.from) params.set("date_from", range.from);
    if (range.to) params.set("date_to", range.to);
    const qs = params.toString();
    api.get(`/stats${qs ? `?${qs}` : ""}`).then((r) => setStats(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [range]);

  const applyPreset = (p) => {
    setPreset(p);
    localStorage.setItem("dash_preset", p);
    setRange(p === "all" ? { from: "", to: "" } : presetRange(p));
  };

  const applyCustom = () => { setPreset("custom"); localStorage.setItem("dash_preset", "custom"); };

  const presets = [
    { k: "all",     label: lang === "de" ? "Alle"         : "All" },
    { k: "today",   label: lang === "de" ? "Heute"        : "Today" },
    { k: "week",    label: lang === "de" ? "Woche"        : "Week" },
    { k: "month",   label: lang === "de" ? "Monat"        : "Month" },
    { k: "quarter", label: lang === "de" ? "Quartal"      : "Quarter" },
    { k: "year",    label: lang === "de" ? "Jahr"         : "Year" },
  ];

  const kpis = [
    { key: "totalQuotes",     label: t("totalQuotes"),     value: stats?.total_quotes ?? 0,                           Icon: FileText },
    { key: "acceptedRevenue", label: t("acceptedRevenue"), value: formatCurrency(stats?.accepted_revenue, lang), Icon: CheckCircle },
    { key: "openVolume",      label: t("openVolume"),      value: formatCurrency(stats?.open_volume, lang),      Icon: Clock },
    { key: "invoicePaid",     label: lang === "de" ? "Rechnungen bezahlt" : "Invoices paid",      value: formatCurrency(stats?.invoice_paid, lang),   Icon: Receipt },
    { key: "invoiceIssued",   label: lang === "de" ? "Offene Rechnungen"  : "Open invoices",      value: formatCurrency(stats?.invoice_issued, lang), Icon: Receipt },
  ];

  const quick = [
    { key: "positions",  count: stats?.positions ?? 0,  Icon: Stack,       to: "/positions" },
    { key: "categories", count: stats?.categories ?? 0, Icon: Tag,         to: "/categories" },
    { key: "customers",  count: stats?.customers ?? 0,  Icon: UsersThree,  to: "/customers" },
  ];

  return (
    <div className="p-6 sm:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow={t("overview")}
        title={t("dashboard")}
        description={lang === "de" ? "KPIs mit Zeitraumfilter — Angebote, Rechnungen & Umsatz auf einen Blick." : "KPIs with time range filter — quotes, invoices & revenue."}
        actions={
          <Link to="/quotes/new">
            <Btn data-testid="dashboard-new-quote"><Plus size={16} />{t("newQuote")}</Btn>
          </Link>
        }
      />

      {/* Time-range bar */}
      <div className="swiss-card p-4 mb-6 flex flex-wrap items-center gap-2">
        <CalendarBlank size={16} className="text-[var(--muted)]" />
        {presets.map((p) => (
          <button key={p.k} onClick={() => applyPreset(p.k)} data-testid={`date-preset-${p.k}`}
            className={`px-3 h-8 text-xs rounded-sm border transition-colors ${preset === p.k ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "bg-[var(--surface)] text-[var(--muted)] border-[var(--border)] hover:bg-[var(--row-hover)]"}`}>
            {p.label}
          </button>
        ))}
        <div className="ml-2 flex items-center gap-2">
          <TextInput type="date" value={range.from} onChange={(e) => { setRange({ ...range, from: e.target.value }); applyCustom(); }} data-testid="date-from" className="h-8 w-auto" />
          <span className="text-xs text-[var(--muted-2)]">–</span>
          <TextInput type="date" value={range.to} onChange={(e) => { setRange({ ...range, to: e.target.value }); applyCustom(); }} data-testid="date-to" className="h-8 w-auto" />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {kpis.map(({ key, label, value, Icon, highlight }) => (
          <div
            key={key}
            data-testid={`kpi-${key}`}
            className="swiss-card p-5"
            style={highlight ? { background: "var(--brand)", borderColor: "var(--brand)", color: "white" } : undefined}
          >
            <div className={`flex items-center justify-between ${highlight ? "text-white/70" : "text-[var(--muted)]"}`}>
              <span className="label-overline">{label}</span>
              <Icon size={18} />
            </div>
            <div className={`mt-3 font-heading text-2xl font-semibold tracking-tight ${highlight ? "text-white" : ""}`}>
              {loading ? "…" : value}
            </div>
          </div>
        ))}
      </div>

      {/* Quick access */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {quick.map(({ key, count, Icon, to }) => (
          <Link key={key} to={to} data-testid={`quick-${key}`} className="swiss-card swiss-card-interactive p-5 flex items-center justify-between">
            <div>
              <div className="label-overline">{t(key)}</div>
              <div className="font-heading text-2xl font-semibold mt-1">{count}</div>
            </div>
            <div className="w-10 h-10 bg-[var(--row-hover)] flex items-center justify-center">
              <Icon size={20} weight="regular" />
            </div>
          </Link>
        ))}
      </div>

      {/* Recent quotes & invoices side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="swiss-card overflow-hidden">
          <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
            <h3 className="font-heading text-lg font-medium">{t("quotes")} — {t("recent")}</h3>
            <Link to="/quotes" className="text-sm text-[var(--brand)] hover:underline">{t("quotes")} →</Link>
          </div>
          {(!stats || stats.recent_quotes.length === 0) ? (
            <div className="p-8 text-center text-[var(--muted)] text-sm">{t("noQuotes")}</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {stats.recent_quotes.map((q) => (
                  <tr key={q.id} className="border-t border-[var(--border)] hover:bg-[var(--row-hover)]">
                    <td className="px-5 py-2.5 font-mono text-xs">{q.number}</td>
                    <td className="px-2 py-2.5 truncate max-w-[180px]">{q.title || "—"}</td>
                    <td className="px-2 py-2.5"><StatusBadge status={q.status} lang={lang} /></td>
                    <td className="px-5 py-2.5 text-right font-medium">{formatCurrency(q.total, lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="swiss-card overflow-hidden">
          <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
            <h3 className="font-heading text-lg font-medium">{t("invoices")} — {t("recent")}</h3>
            <Link to="/invoices" className="text-sm text-[var(--brand)] hover:underline">{t("invoices")} →</Link>
          </div>
          {(!stats || !stats.recent_invoices || stats.recent_invoices.length === 0) ? (
            <div className="p-8 text-center text-[var(--muted)] text-sm">{t("noInvoices")}</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {stats.recent_invoices.map((q) => (
                  <tr key={q.id} className="border-t border-[var(--border)] hover:bg-[var(--row-hover)]">
                    <td className="px-5 py-2.5 font-mono text-xs">{q.number}</td>
                    <td className="px-2 py-2.5 truncate max-w-[180px]">{q.title || "—"}</td>
                    <td className="px-2 py-2.5"><StatusBadge status={q.status} lang={lang} /></td>
                    <td className="px-5 py-2.5 text-right font-medium">{formatCurrency(q.total, lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
